class NewsletterPopup extends ModalDialog {
  constructor() {
    super();
    this.delay = Math.max(Number(this.dataset.delay) || 12, 4) * 1000;
    this.scrollThreshold = Number(this.dataset.scrollThreshold) || 45;
    this.dismissedDays = Number(this.dataset.dismissedDays) || 7;
    this.subscribedDays = Number(this.dataset.subscribedDays) || 365;
    this.minimumEngagementDelay = 4000;
    this.connectedAt = performance.now();
    this.dismissedCookie = 'lc-newsletter-dismissed-v3';
    this.subscribedCookie = 'lc-newsletter-subscribed-v3';
    this.success = this.querySelector('[data-newsletter-success]');
    this.error = this.querySelector('[data-newsletter-error]');
    this.emailInput = this.querySelector('[data-typewriter-placeholder]');
    this.copyButton = this.querySelector('[data-copy-discount]');
    this.copyStatus = this.querySelector('[data-copy-status]');
    this.showTimer = null;
    this.placeholderTimer = null;
    this.isInitialized = false;
    this.typewriterStarted = false;

    this.onScroll = this.onScroll.bind(this);
    this.onVisibilityChange = this.onVisibilityChange.bind(this);
    this.onCopy = this.onCopy.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
  }

  connectedCallback() {
    if (this.isInitialized) return;
    this.isInitialized = true;

    this.copyButton?.addEventListener('click', this.onCopy);
    document.addEventListener('keydown', this.onKeyDown);
    if (this.success) {
      setCookie(this.subscribedCookie, 'true', this.subscribedDays);
      requestAnimationFrame(() => {
        this.show();
        this.success.focus();
      });
      return;
    }

    if (this.error || this.dataset.openInDesignMode === 'true') {
      requestAnimationFrame(() => this.show());
      return;
    }

    if (this.dataset.enable !== 'true' || !this.canOpen()) return;

    this.showTimer = window.setTimeout(
      () => this.openWhenVisible(),
      this.delay
    );
    window.addEventListener('scroll', this.onScroll, { passive: true });
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  disconnectedCallback() {
    window.clearTimeout(this.showTimer);
    window.clearTimeout(this.placeholderTimer);
    window.removeEventListener('scroll', this.onScroll);
    document.removeEventListener(
      'visibilitychange',
      this.onVisibilityChange
    );
    this.copyButton?.removeEventListener('click', this.onCopy);
    document.removeEventListener('keydown', this.onKeyDown);
  }

  canOpen() {
    return (
      getCookie(this.dismissedCookie) === null &&
      getCookie(this.subscribedCookie) === null
    );
  }

  onScroll() {
    const scrollableHeight =
      document.documentElement.scrollHeight - window.innerHeight;
    if (scrollableHeight <= 0) return;

    const progress = (window.scrollY / scrollableHeight) * 100;
    if (progress < this.scrollThreshold) return;

    window.removeEventListener('scroll', this.onScroll);
    window.clearTimeout(this.showTimer);

    const remainingDelay = Math.max(
      this.minimumEngagementDelay - (performance.now() - this.connectedAt),
      0
    );
    this.showTimer = window.setTimeout(
      () => this.openWhenVisible(),
      remainingDelay
    );
  }

  onVisibilityChange() {
    if (document.visibilityState === 'visible' && this.pendingOpen) {
      this.pendingOpen = false;
      this.openWhenVisible();
    }
  }

  openWhenVisible() {
    if (document.visibilityState !== 'visible') {
      this.pendingOpen = true;
      return;
    }

    if (!this.canOpen() || this.hasAttribute('open')) return;

    window.removeEventListener('scroll', this.onScroll);
    document.removeEventListener(
      'visibilitychange',
      this.onVisibilityChange
    );
    this.show();
  }

  show(opener) {
    super.show(opener);
    this.setupTypewriter();
    window.requestAnimationFrame(() => {
      this.querySelector('[id^="ModalClose-"]')?.focus({
        preventScroll: true
      });
    });
  }

  onKeyDown(event) {
    if (!this.hasAttribute('open')) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.hide();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusableElements = Array.from(
      this.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter(element => element.getClientRects().length > 0);

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    if (!firstElement || !lastElement) return;

    if (!this.contains(document.activeElement)) {
      event.preventDefault();
      (event.shiftKey ? lastElement : firstElement).focus();
    } else if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  }

  hide() {
    if (!this.hasAttribute('open')) return;
    super.hide();

    if (
      this.dataset.openInDesignMode !== 'true' &&
      !this.success
    ) {
      setCookie(this.dismissedCookie, 'true', this.dismissedDays);
    }
  }

  setupTypewriter() {
    if (!this.emailInput || this.typewriterStarted) return;
    this.typewriterStarted = true;

    const example = this.emailInput.dataset.typewriterPlaceholder;
    if (!example) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.emailInput.placeholder = example;
      return;
    }

    let characterIndex = 0;
    this.emailInput.placeholder = '';
    this.emailInput.classList.add('is-typing');

    const typeCharacter = () => {
      if (this.emailInput.value || document.activeElement === this.emailInput) {
        this.emailInput.placeholder = example;
        this.emailInput.classList.remove('is-typing');
        return;
      }

      characterIndex += 1;
      this.emailInput.placeholder = example.slice(0, characterIndex);
      if (characterIndex < example.length) {
        this.placeholderTimer = window.setTimeout(typeCharacter, 105);
      } else {
        this.emailInput.classList.remove('is-typing');
      }
    };

    this.placeholderTimer = window.setTimeout(typeCharacter, 280);
    this.emailInput.addEventListener(
      'focus',
      () => {
        window.clearTimeout(this.placeholderTimer);
        this.emailInput.placeholder = example;
        this.emailInput.classList.remove('is-typing');
      },
      { once: true }
    );
  }

  async onCopy() {
    const code = this.copyButton.dataset.copyDiscount;
    if (!code) return;

    try {
      await navigator.clipboard.writeText(code);
    } catch (error) {
      const codeInput = this.querySelector('.newsletter-popup__code-row input');
      codeInput?.select();
      codeInput?.setSelectionRange(0, code.length);
      document.execCommand('copy');
    }

    const copiedLabel = this.copyButton.dataset.copiedLabel;
    const defaultLabel = this.copyButton.dataset.defaultLabel;
    this.copyButton.textContent = copiedLabel;
    if (this.copyStatus) this.copyStatus.textContent = copiedLabel;

    window.setTimeout(() => {
      this.copyButton.textContent = defaultLabel;
      if (this.copyStatus) this.copyStatus.textContent = '';
    }, 2400);
  }
}

customElements.define('newsletter-popup', NewsletterPopup);
